/**
 * ShopService
 * Service for public shop endpoints (read-only)
 * Route: api/shop
 * Controller: ShopController.cs (CCMS.Api.EventManagement)
 */
import { BaseService } from '../base/BaseService';
/**
 * ShopService
 * Public read-only endpoints for shop event and vendor data.
 * All data is served from Redis cache on the backend.
 */
export class ShopService extends BaseService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? ShopService.DEFAULT_SERVICE_PATH,
            basePath: ShopService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    // ==================== EVENT ENDPOINTS ====================
    /**
     * Get all events with their confirmed vendors
     * GET /api/shop/events
     */
    async getEvents() {
        const response = await this.getEventsRaw();
        return response.data ?? [];
    }
    /**
     * Get all events (raw response)
     */
    getEventsRaw() {
        const endpoint = this.getEndpoint('events', 'events');
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    /**
     * Get a single event by ID with its confirmed vendors
     * GET /api/shop/events/{id}
     */
    async getEventById(id) {
        const response = await this.getEventByIdRaw(id);
        if (!response.data) {
            throw new Error(`Event ${id} not found`);
        }
        return response.data;
    }
    /**
     * Get event by ID (raw response)
     */
    getEventByIdRaw(id) {
        const endpoint = this.getEndpoint('event-by-id', `events/${id}`);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    /**
     * Get upcoming events (StartDate > now)
     * GET /api/shop/events/upcoming
     */
    async getUpcomingEvents() {
        const response = await this.getUpcomingEventsRaw();
        return response.data ?? [];
    }
    /**
     * Get upcoming events (raw response)
     */
    getUpcomingEventsRaw() {
        const endpoint = this.getEndpoint('events-upcoming', 'events/upcoming');
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    /**
     * Get ongoing events (now >= StartDate AND now <= EndDate)
     * GET /api/shop/events/ongoing
     */
    async getOngoingEvents() {
        const response = await this.getOngoingEventsRaw();
        return response.data ?? [];
    }
    /**
     * Get ongoing events (raw response)
     */
    getOngoingEventsRaw() {
        const endpoint = this.getEndpoint('events-ongoing', 'events/ongoing');
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    /**
     * Get events filtered by location
     * GET /api/shop/events/by-location/{locationId}
     */
    async getEventsByLocation(locationId) {
        const response = await this.getEventsByLocationRaw(locationId);
        return response.data ?? [];
    }
    /**
     * Get events by location (raw response)
     */
    getEventsByLocationRaw(locationId) {
        const endpoint = this.getEndpoint('events-by-location', `events/by-location/${locationId}`);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    /**
     * Get events filtered by type
     * GET /api/shop/events/by-type/{typeId}
     */
    async getEventsByType(typeId) {
        const response = await this.getEventsByTypeRaw(typeId);
        return response.data ?? [];
    }
    /**
     * Get events by type (raw response)
     */
    getEventsByTypeRaw(typeId) {
        const endpoint = this.getEndpoint('events-by-type', `events/by-type/${typeId}`);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    // ==================== VENDOR ENDPOINTS ====================
    /**
     * Get all active/approved vendors
     * GET /api/shop/vendors
     */
    async getVendors() {
        const response = await this.getVendorsRaw();
        return response.data ?? [];
    }
    /**
     * Get all vendors (raw response)
     */
    getVendorsRaw() {
        const endpoint = this.getEndpoint('vendors', 'vendors');
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    /**
     * Get a single vendor by ID with social media links
     * GET /api/shop/vendors/{id}
     */
    async getVendorById(id) {
        const response = await this.getVendorByIdRaw(id);
        if (!response.data) {
            throw new Error(`Vendor ${id} not found`);
        }
        return response.data;
    }
    /**
     * Get vendor by ID (raw response)
     */
    getVendorByIdRaw(id) {
        const endpoint = this.getEndpoint('vendor-by-id', `vendors/${id}`);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
}
ShopService.DEFAULT_SERVICE_PATH = 'events';
ShopService.BASE_PATH = 'api/shop';
//# sourceMappingURL=ShopService.js.map